import { useMemo } from 'react';
import { useModelContextWindows } from './useModelContextWindows';

// Conservative fallback for when no context window is configured
const DEFAULT_FALLBACK = { 
  contextWindow: 8000, 
  softLimit: 6000, 
  hardLimit: 7500 
};

// Rough token estimation (more accurate than simple word count)
const estimateTokens = (text) => {
  if (!text) return 0;
  
  // Rough estimation: 1 token ≈ 4 characters for English text
  // This accounts for punctuation, spaces, and common patterns
  return Math.ceil(text.length / 4);
};

const estimateMessageTokens = (message) => {
  let tokens = 0;
  
  // Text content
  if (message.text) {
    tokens += estimateTokens(message.text);
  }
  
  // System message overhead
  tokens += 10;
  
  // File attachments (rough estimation)
  if (message.files && message.files.length > 0) {
    tokens += message.files.length * 1000; // Rough estimate per file
  }
  
  // Images (rough estimation)
  if (message.images && message.images.length > 0) {
    tokens += message.images.length * 1000; // Rough estimate per image
  }
  
  return tokens;
};

export const useChatLength = (messages = [], selectedModel = null, debugMode = null) => {
  const { getModelConfig } = useModelContextWindows();
  
  // Analyze the conversation to determine the most restrictive context window
  const conversationAnalysis = useMemo(() => {
    if (!messages || messages.length === 0) {
      return { selectedModelConfig: getModelConfig(selectedModel), modelsUsed: [], strategy: 'selected' };
    }

    // Extract unique models used in the conversation
    const modelsUsed = new Set();
    messages.forEach(message => {
      if (message.provider && message.model) {
        modelsUsed.add(`${message.provider}-${message.model}`);
      }
      // Also check responses array for multiple response variants
      if (message.responses && Array.isArray(message.responses)) {
        message.responses.forEach(response => {
          if (response.provider && response.model) {
            modelsUsed.add(`${response.provider}-${response.model}`);
          }
        });
      }
    });

    const uniqueModels = Array.from(modelsUsed);
    const selectedModelConfig = getModelConfig(selectedModel);

    // If only one model was used in the conversation, use that model's limits
    if (uniqueModels.length === 1) {
      const conversationModel = uniqueModels[0];
      const conversationModelConfig = getModelConfig(conversationModel);
      return {
        selectedModelConfig,
        conversationModelConfig,
        modelsUsed: uniqueModels,
        strategy: 'conversation',
        primaryModel: conversationModel
      };
    }

    // If multiple models were used, we need to be conservative
    if (uniqueModels.length > 1) {
      // Get configs for all models used
      const modelConfigs = uniqueModels.map(model => ({
        model,
        config: getModelConfig(model)
      })).filter(item => item.config && item.config.contextWindow > 0);

      if (modelConfigs.length === 0) {
        return { selectedModelConfig, modelsUsed: uniqueModels, strategy: 'selected' };
      }

      // Find the most restrictive (smallest) context window among models used
      const mostRestrictive = modelConfigs.reduce((min, current) => {
        return current.config.contextWindow < min.config.contextWindow ? current : min;
      });

      return {
        selectedModelConfig,
        conversationModelConfig: mostRestrictive.config,
        modelsUsed: uniqueModels,
        strategy: 'conservative',
        primaryModel: mostRestrictive.model,
        allModelConfigs: modelConfigs
      };
    }

    // Fallback to selected model
    return { selectedModelConfig, modelsUsed: [], strategy: 'selected' };
  }, [messages, selectedModel, getModelConfig]);

  const modelConfig = useMemo(() => {
    const { selectedModelConfig, conversationModelConfig, strategy } = conversationAnalysis;
    
    // Determine which config to use based on strategy
    let activeConfig;
    switch (strategy) {
      case 'conversation':
      case 'conservative':
        activeConfig = conversationModelConfig || selectedModelConfig;
        break;
      case 'selected':
      default:
        activeConfig = selectedModelConfig;
        break;
    }
    
    // If model has no context window (like image generation models), return null
    if (!activeConfig || !activeConfig.contextWindow || activeConfig.contextWindow <= 0) {
      return null;
    }
    
    return {
      ...activeConfig,
      strategy: conversationAnalysis.strategy,
      modelsUsed: conversationAnalysis.modelsUsed,
      primaryModel: conversationAnalysis.primaryModel
    };
  }, [conversationAnalysis]);

  const chatStats = useMemo(() => {
    // If model doesn't have context window (like image generation), return null
    if (!modelConfig) {
      return null;
    }
    
    // Debug mode overrides for testing
    if (debugMode) {
      const debugConfigs = {
        'approaching': { messageCount: 25, totalTokens: Math.floor(modelConfig.softLimit * 0.85) },
        'warning': { messageCount: 40, totalTokens: Math.floor(modelConfig.softLimit * 1.1) },
        'blocked': { messageCount: 60, totalTokens: Math.floor(modelConfig.hardLimit * 1.05) },
        // Multi-model debug scenarios
        'multi-conservative': { 
          messageCount: 30, 
          totalTokens: Math.floor(modelConfig.softLimit * 0.9),
          mockMultiModel: true,
          mockModelsUsed: ['openai-gpt-4o', 'anthropic-claude-3-5-sonnet', 'google-gemini-1.5-pro'],
          mockStrategy: 'conservative',
          mockPrimaryModel: 'openai-gpt-4o'
        },
        'multi-conversation': {
          messageCount: 20,
          totalTokens: Math.floor(modelConfig.softLimit * 0.7),
          mockMultiModel: false,
          mockModelsUsed: ['anthropic-claude-3-5-sonnet'],
          mockStrategy: 'conversation',
          mockPrimaryModel: 'anthropic-claude-3-5-sonnet'
        }
      };
      
      if (debugConfigs[debugMode]) {
        const debug = debugConfigs[debugMode];
        return {
          totalTokens: debug.totalTokens,
          contextWindow: modelConfig.contextWindow,
          softLimit: modelConfig.softLimit,
          hardLimit: modelConfig.hardLimit,
          messageCount: debug.messageCount,
          softLimitPercentage: (debug.totalTokens / modelConfig.softLimit) * 100,
          hardLimitPercentage: (debug.totalTokens / modelConfig.hardLimit) * 100,
          contextPercentage: (debug.totalTokens / modelConfig.contextWindow) * 100,
          status: debugMode.includes('multi-') ? 'warning' : debugMode,
          severity: debugMode === 'blocked' ? 'error' : 'warning',
          isApproachingLimit: debugMode === 'approaching',
          isWarning: debugMode === 'warning' || debugMode.includes('multi-'),
          isBlocked: debugMode === 'blocked',
          remainingTokens: Math.max(0, modelConfig.hardLimit - debug.totalTokens),
          modelName: selectedModel || 'debug-mode',
          // Multi-model debug data
          strategy: debug.mockStrategy || modelConfig.strategy,
          modelsUsed: debug.mockModelsUsed || modelConfig.modelsUsed || [],
          primaryModel: debug.mockPrimaryModel || modelConfig.primaryModel,
          isMultiModel: debug.mockMultiModel !== undefined ? debug.mockMultiModel : (modelConfig.modelsUsed || []).length > 1,
          contextSource: debug.mockStrategy === 'conservative' ? 'most_restrictive' : 
                       debug.mockStrategy === 'conversation' ? 'conversation_model' : 'selected_model',
          isDebugMode: true
        };
      }
    }

    const totalTokens = messages.reduce((sum, message) => {
      return sum + estimateMessageTokens(message);
    }, 0);
    
    const { contextWindow, softLimit, hardLimit } = modelConfig;
    
    // Calculate percentages
    const softLimitPercentage = (totalTokens / softLimit) * 100;
    const hardLimitPercentage = (totalTokens / hardLimit) * 100;
    const contextPercentage = (totalTokens / contextWindow) * 100;
    
    // Determine status
    let status = 'normal';
    let severity = 'info';
    
    if (totalTokens >= hardLimit) {
      status = 'blocked';
      severity = 'error';
    } else if (totalTokens >= softLimit) {
      status = 'warning';
      severity = 'warning';
    } else if (totalTokens >= softLimit * 0.8) {
      status = 'approaching';
      severity = 'info';
    }
    
    return {
      totalTokens,
      contextWindow,
      softLimit,
      hardLimit,
      softLimitPercentage,
      hardLimitPercentage,
      contextPercentage,
      status,
      severity,
      messageCount: messages.length,
      isApproachingLimit: status === 'approaching',
      isWarning: status === 'warning',
      isBlocked: status === 'blocked',
      remainingTokens: Math.max(0, hardLimit - totalTokens),
      modelName: selectedModel,
      // Multi-model conversation info
      strategy: modelConfig.strategy,
      modelsUsed: modelConfig.modelsUsed || [],
      primaryModel: modelConfig.primaryModel,
      isMultiModel: (modelConfig.modelsUsed || []).length > 1,
      contextSource: modelConfig.strategy === 'conservative' ? 'most_restrictive' : 
                   modelConfig.strategy === 'conversation' ? 'conversation_model' : 'selected_model'
    };
  }, [messages, modelConfig, selectedModel, debugMode]);

  return chatStats;
};

export { DEFAULT_FALLBACK, estimateTokens, estimateMessageTokens };